import { defineStore } from 'pinia';
import { ref, computed, watch, nextTick } from 'vue';

export type ThemePreference = 'light' | 'dark' | 'system';
export type EffectiveTheme = 'light' | 'dark';

const STORAGE_KEY = 'thor_theme';

function getSystemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function loadStoredTheme(): ThemePreference {
  if (typeof localStorage === 'undefined') return 'system';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'system';
}

function applyToDom(isDark: boolean) {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.classList.toggle('dark', isDark);
  html.style.colorScheme = isDark ? 'dark' : 'light';
}

export const useThemeStore = defineStore('theme', () => {
  const theme = ref<ThemePreference>(loadStoredTheme());
  const systemPrefersDark = ref(getSystemPrefersDark());

  const effectiveTheme = computed<EffectiveTheme>(() => {
    if (theme.value === 'system') {
      return systemPrefersDark.value ? 'dark' : 'light';
    }
    return theme.value;
  });

  function setTheme(value: ThemePreference) {
    theme.value = value;
    localStorage.setItem(STORAGE_KEY, value);
    const isDark = value === 'dark' || (value === 'system' && systemPrefersDark.value);
    applyToDom(isDark);
    // Re-apply after Vue flush so we win over any stale watch or re-render
    nextTick(() => applyToDom(effectiveTheme.value === 'dark'));
  }

  // Listen to OS preference changes so effectiveTheme updates when theme is 'system'
  if (typeof window !== 'undefined') {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', () => {
      systemPrefersDark.value = mq.matches;
    });
  }

  // Apply whenever effectiveTheme changes
  watch(
    effectiveTheme,
    (effective) => {
      applyToDom(effective === 'dark');
    },
    { immediate: true }
  );

  return {
    theme,
    effectiveTheme,
    setTheme,
  };
});
